import { firestore } from "firebase";
import {
  DryingQualityRating,
  DryingQualityRatingAttributeNames,
} from "./DryingQualityRating";
import {
  ProgramQualityRating,
  ProgramPhaseRatingAttributeNames,
} from "./ProgramQualityRating";
import {
  OverallDryingRating,
  OverallDryingRatingAttributeNames,
} from "./OverallDryingRating";
import { joinToString } from "@/utils/LocalisationHelper";
import { DryingDurationRating } from "./DryingDurationRating";
import { NotesDryingRating, NotesDryingRatingAttributeNames } from "./NotesDryingRating";
import { isRoomDryingProgram } from "./ComBox";

export enum DryingLabel {
  NO_PLASTIC_SHEETING_ON_RAW_FLOOR = "NO_PLASTIC_SHEETING_ON_RAW_FLOOR",
  UNDERFLOOR_HEATING = "UNDERFLOOR_HEATING",

  NEW_MOISTURE_APPEARED = "NEW_MOISTURE_APPEARED",
  NIGHTLY_INTERRUPTION_BY_CLIENT = "NIGHTLY_INTERRUPTION_BY_CLIENT",

  INSUFFICIENT_AIR_FLOW = "INSUFFICIENT_AIR_FLOW",
  RELAY_MALFUNCTION = "RELAY_MALFUNCTION",

  MOISTURE_NEAR_DRILL_HOLES = "MOISTURE_NEAR_DRILL_HOLES",
  DRYING_WITH_PRE_DRIED_AIR = "DRYING_WITH_PRE_DRIED_AIR",
}

export interface ProgramChainQualityRating {
  createdAt: firestore.Timestamp;
  createdByClientId: string;
  createdByUserId: string;
  createdByUserNickname: string;
  note?: string;
  dryingQualityRating?: DryingQualityRating;
  programQualityRating?: ProgramQualityRating;
  overallDryingRating?: OverallDryingRating;
  dryingDurationRating?: DryingDurationRating;
  labels: DryingLabel[];
  notesDryingRating: NotesDryingRating;
}

const ratingOptions = ["veryBad", "bad", "ok", "good", "veryGood"];
const ratingOptionsForDryingDuration = ["tooShort", "short", "good", "long", "tooLong"];
  
function getRatingOptions(attribute: string) {
  switch(attribute) {
    case "anomalies":
      return ["relayMalfunction", "newMoistureAppeared", "powerConsumptionTooLow"];
    case "general":
      return ["underfloorHeating", "interruptionByCustomers"];
    case "roomConditions":
      return ["roomHumidityTooHigh", "gkgRoomTooHigh", "gkgConstant", "stableAfterXDays"];
    case "roomTemperature":
      return ["tooLow", "tooHigh"];
    case "EDTGeneral":
      return ["noPlasticSheetingOnRawFloor", "insufficientAirFlow", "moistureNearDrillHoles", "dryingWithPreDriedAir"];
    case "EDTPhase4":
      return ["humidityOfProcessAirTooHigh", "gkgOfProcessAirTooHigh"];
    case "EDTPauses":
      return ["gkgDecreasedInPause", "gkgConstantInPause", "gkgIncreasedInPause", "gkgIncreasedAfterPause"];
  } 

  return [];
}

export function formatPhase(vue: Vue, phase: string) {
  return phase == "postDrying"
    ? vue.$t("rateDrying.postDryingRun")
    : vue.$t("rateDrying.formatPhaseShort", [phase]);
}

export interface FormattedRating {
  label: string;
  rating: string;
}

export function formatProgramQualityRating(
  vue: Vue,
  programQualityRating: ProgramQualityRating,
  programNumber: number
) {
  const phaseRatings = programQualityRating.phaseRatings;
  const ratings = new Map<string, string>();

  const phaseRatingsKeys = Object.keys(phaseRatings);

  if (phaseRatingsKeys.length > 0) {
    const phaseRating = phaseRatings[1].ratings;

    ProgramPhaseRatingAttributeNames.forEach((attribute) => {
      const rating = phaseRating[attribute];

      if (attribute === "phaseChange" && isRoomDryingProgram(programNumber)) {
        return;
      }

      if (!rating && rating !== 0) {
        return;
      }

      //const phaseDescription = formatPhase(vue, phaseRatingsKeys[0]);
      let ratingDescription = "";

      if (rating < 0) {
        ratingDescription = vue
          .$t(
            `rateDrying.qualityProgram.${
              attribute === "phaseChange" ? "toSlow" : "toLong"
            }`
          )
          .toString();
      }

      if (rating > 0) {
        ratingDescription = vue
          .$t(
            `rateDrying.qualityProgram.${
              attribute === "phaseChange" ? "toFast" : "toShort"
            }`
          )
          .toString();
      }

      if (rating === 0) {
        ratingDescription = vue
          .$t(`rateDrying.qualityProgram.ok`)
          .toString();
      }

      //const phaseRatingDescriptionOld = `${phaseDescription}: ${ratingDescription}`;
      const phaseRatingDescription = ratingDescription;

      ratings.set(
        attribute,
        joinToString([ratings.get(attribute), phaseRatingDescription], ", ")
      );
    });
  }

  const result: FormattedRating[] = [];

  ProgramPhaseRatingAttributeNames.forEach((attribute) => {
    const attributeDescription = ratings.get(attribute);

    if (attributeDescription) {
      result.push({
        label: vue.$t(`rateDrying.qualityProgram.${attribute}`).toString(),
        rating: attributeDescription,
      });
    }
  });

  return result.length === 0 ? null : result;
}

export function formatDryingQualityRating(
  vue: Vue,
  dryingQualityRating: DryingQualityRating
) {
  const result: FormattedRating[] = [];

  DryingQualityRatingAttributeNames.forEach((attribute) => {
    const value = dryingQualityRating[attribute];

    if (value != null) {
      const ratingDescription = vue
        .$t(`rateDrying.qualityDrying.${ratingOptions[value + 2]}`)
        .toString();

      const ratingLabel = vue
        .$t(`rateDrying.qualityDrying.${attribute}`)
        .toString();

      result.push({
        label: ratingLabel,
        rating: ratingDescription,
      });
    }
  });

  return result.length == 0 ? null : result;
}

export function formatOverallDryingRating(
  vue: Vue,
  overallDryingRating: OverallDryingRating
): FormattedRating[] | null {
  const result: FormattedRating[] = [];

  OverallDryingRatingAttributeNames.forEach((attribute) => {
    const value = overallDryingRating[attribute];

    if (value != null) {
      const ratingDescription = attribute !== "dryingDuration"
      ? vue
          .$t(`rateDrying.qualityDrying.${ratingOptions[value + 2]}`)
          .toString()
      : vue
          .$t(`rateDrying.qualityDrying.${ratingOptionsForDryingDuration[value + 2]}`)
          .toString();
      

      const ratingLabel = vue
        .$t(`rateDrying.qualityOverall.${attribute}`)
        .toString();

      result.push({
        label: ratingLabel,
        rating: ratingDescription,
      });
    }
  });

  return result.length === 0 ? null : result;
}

export function formatOverallQualityNotes(
  vue: Vue,
  overallQualityNotes: NotesDryingRating
): FormattedRating[] | null {
  const result: FormattedRating[] = [];

  NotesDryingRatingAttributeNames.forEach((attribute) => {
    const values = overallQualityNotes[attribute];

    if (values != null && values.length !== 0) {
      let ratingDescription = "";

      values.forEach((value) => {
        if (ratingDescription !== "") {
          ratingDescription += ", ";
        }

        ratingDescription += vue
          .$t(`rateDrying.qualityNotes.${getRatingOptions(attribute)[value]}`)
          .toString();
      })

      const ratingLabel = vue
        .$t(`rateDrying.qualityNotes.${attribute}`)
        .toString();

      result.push({
        label: ratingLabel,
        rating: ratingDescription,
      });
    }
  });

  return result.length === 0 ? null : result;
}

export function localizeDryingLabel(vue: Vue, dryingLabel: DryingLabel) {
  const prefix = "rateDrying.qualityOverall.labels.";
  switch (dryingLabel) {
    case DryingLabel.NO_PLASTIC_SHEETING_ON_RAW_FLOOR:
      return vue.$t(`${prefix}noPlasticSheetingOnRawFloor`);
    case DryingLabel.UNDERFLOOR_HEATING:
      return vue.$t(`${prefix}underfloorHeating`);
    case DryingLabel.NEW_MOISTURE_APPEARED:
      return vue.$t(`${prefix}newMoistureAppeared`);
    case DryingLabel.NIGHTLY_INTERRUPTION_BY_CLIENT:
      return vue.$t(`${prefix}nightlyInterruptionByClient`);
    case DryingLabel.INSUFFICIENT_AIR_FLOW:
      return vue.$t(`${prefix}insufficientAirFlow`);
    case DryingLabel.RELAY_MALFUNCTION:
      return vue.$t(`${prefix}relayMalfunction`);
    case DryingLabel.MOISTURE_NEAR_DRILL_HOLES:
      return vue.$t(`${prefix}moistureNearDrillHoles`);
    case DryingLabel.DRYING_WITH_PRE_DRIED_AIR:
      return vue.$t(`${prefix}dryingWithPreDriedAir`);
  }
}

export function formatDryingLabels(vue: Vue, labels: DryingLabel[]) {
  if (labels.length === 0) {
    return null;
  }

  return labels.map((label) => localizeDryingLabel(vue, label)).join(", ");
}
